import {
  Component,
  effect,
  inject,
  signal,
  untracked,
  OnInit,
  OnDestroy
} from '@angular/core';

import { Store } from '@ngxs/store';
import { timer, take, tap, Subject, takeUntil } from 'rxjs';

import { ModalService } from '@app/core/ui/components/modal';
import {
  ModalData,
  MODAL_DATA
} from '@app/core/ui/components/modal/modal.component';
import { ToggleCartDrawerAction } from '@app/features/cart-checkout/store/cart/cart-ui.actions';
import { ClearCartAction } from '@app/features/cart-checkout/store/cart/cart.action';
import { ResetLoadingAction } from '@app/features/cart-checkout/store/cart-checkout/cart-checkout.actions';

type Status = 'counting' | 'redirecting' | 'redirected' | 'blocked';

const REDIRECT_DELAY_MS = 300;
const COUNTDOWN_START = 7;
const COUNTDOWN_INTERVAL_MS = 1000;

@Component({
  standalone: true,
  selector: 'mw-buy-confirmation-modal',
  templateUrl: './buy-confirmation-modal.component.html',
  styleUrl: './buy-confirmation-modal.component.scss'
})
export class BuyConfirmationModalComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly modal = inject(ModalService);
  private readonly data =
    inject<ModalData<{ redirectUrl: string }>>(MODAL_DATA);

  private readonly destroy$ = new Subject<void>();
  private readonly timeoutIds = new Set<number>();
  private readonly countdownSubscription = new Subject<void>();

  readonly countdown = signal(COUNTDOWN_START);
  readonly status = signal<Status>('counting');
  readonly redirectUrl = signal(this.data?.redirectUrl || '');
  private redirectAttempted = false;
  private isManualRedirect = false;

  constructor() {
    effect(() => {
      if (
        this.status() === 'redirecting' &&
        !this.redirectAttempted &&
        !this.isManualRedirect
      ) {
        untracked(() => this.executeRedirect());
      }
    });
  }

  ngOnInit() {
    this.startCountdown();
  }

  ngOnDestroy() {
    this.cleanup();
  }

  attemptRedirect() {
    if (this.redirectAttempted) {
      return;
    }

    if (this.isManualRedirect) {
      this.redirectUrl.set(
        this.redirectUrl().split('callbackUrl=').pop() || ''
      );
    }

    this.isManualRedirect = true;

    this.stopCountdown();
    this.executeRedirect();
  }

  handleClose() {
    if (this.shouldRestoreCartAfterClose()) {
      this.store.dispatch([
        new ResetLoadingAction(),
        new ToggleCartDrawerAction(true)
      ]);
    }

    this.modal.close();
  }

  private shouldRestoreCartAfterClose(): boolean {
    if (this.isManualRedirect) {
      return false;
    }

    if (this.status() === 'redirected' || this.status() === 'redirecting') {
      return false;
    }

    return true;
  }

  private startCountdown() {
    timer(COUNTDOWN_INTERVAL_MS, COUNTDOWN_INTERVAL_MS)
      .pipe(
        take(this.countdown()),
        tap(() => this.updateCountdown()),
        takeUntil(this.destroy$),
        takeUntil(this.countdownSubscription)
      )
      .subscribe();
  }

  private stopCountdown() {
    this.countdownSubscription.next();
    this.countdownSubscription.complete();
  }

  private updateCountdown() {
    this.countdown.update((c) => c - 1);

    if (this.countdown() > 0) {
      this.status.set('counting');
    } else if (
      !['redirecting', 'redirected', 'blocked'].includes(this.status())
    ) {
      this.status.set('redirecting');
    }
  }

  private executeRedirect() {
    this.redirectAttempted = true;
    this.status.set('redirecting');

    try {
      // noopener faz window.open retornar null mesmo com sucesso (Safari/Chrome).
      // Não usar o retorno para detectar bloqueio de popup.
      window.open(this.redirectUrl(), '_blank', 'noopener,noreferrer');

      this.setSafeTimeout(() => {
        this.status.set('redirected');
        this.store.dispatch(new ClearCartAction());
      }, REDIRECT_DELAY_MS);
    } catch (error) {
      console.error('Redirect error:', error);
      this.status.set('blocked');
    } finally {
      this.resetRedirectAttempt();
    }
  }

  private resetRedirectAttempt() {
    this.setSafeTimeout(() => {
      this.redirectAttempted = false;
    }, REDIRECT_DELAY_MS);
  }

  private setSafeTimeout(callback: () => void, delay: number) {
    const id = window.setTimeout(() => {
      callback();
      this.timeoutIds.delete(id);
    }, delay);

    this.timeoutIds.add(id);
    return id;
  }

  private cleanup() {
    this.destroy$.next();
    this.destroy$.complete();
    this.countdownSubscription.complete();
    this.clearAllTimeouts();
  }

  private clearAllTimeouts() {
    this.timeoutIds.forEach((id) => clearTimeout(id));
    this.timeoutIds.clear();
  }
}
